import { useQuery } from "@tanstack/react-query";
import type { MediumFinancialTransparencyData } from "gsintegrations";
import { mediumRepository } from "../repositories/mediumRepository";

/**
 * Query key for medium financial transparency.
 */
export const mediumFinancialTransparencyQueryKey = (mediumId: string) =>
  ["medium", mediumId, "financial-transparency"] as const;

interface UseMediumFinancialTransparencyQueryProps {
  mediumId?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch financial transparency data for a medium.
 *
 * Returns payment status, shared expenses, contribution impact,
 * and formatted values for display.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useMediumFinancialTransparencyQuery({
 *   mediumId: 'abc-123',
 *   enabled: !!mediumId,
 * });
 * ```
 */
export function useMediumFinancialTransparencyQuery({
  mediumId,
  enabled = true,
}: UseMediumFinancialTransparencyQueryProps) {
  return useQuery<MediumFinancialTransparencyData>({
    queryKey: mediumFinancialTransparencyQueryKey(mediumId ?? ""),
    queryFn: () => mediumRepository.getFinancialTransparency(mediumId!),
    enabled: enabled && !!mediumId,
    staleTime: 1000 * 60 * 5, // 5 minutes - data doesn't change frequently
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 2,
    refetchOnMount: false,
  });
}

export default useMediumFinancialTransparencyQuery;
